import { Bell, Send, MessageSquare, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { formatNumber, formatDateTime } from '../lib/utils';
import { notificationsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';

export default function Notifications() {
  const [channel, setChannel] = useState('in_app');
  const [segment, setSegment] = useState('all');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [summary, setSummary] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => {
    notificationsAPI.summary()
      .then((res) => setSummary(res.data))
      .catch(() => toast.error('Failed to load notifications'));
  };

  useEffect(() => { load(); }, []);

  const send = async () => {
    try {
      setSending(true);
      const res = await notificationsAPI.send({ title, message, segment, channel });
      toast.success(res.data.message || 'Sent');
      setTitle('');
      setMessage('');
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="notifications-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Notification Center</h1>
        <p className="page-subtitle">Send notifications and manage communication</p>
      </div>

      {/* Stats */}
      <div className="widget-grid mb-6">
        <div className="stat-card">
          <Bell className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{formatNumber(summary?.sent_today || 0)}</p>
          <p className="stat-label">In-app alerts today</p>
        </div>
        <div className="stat-card">
          <Users className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{formatNumber(summary?.players || 0)}</p>
          <p className="stat-label">Players</p>
        </div>
        <div className="stat-card">
          <Send className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{formatNumber(summary?.broadcasts?.length || 0)}</p>
          <p className="stat-label">Broadcasts</p>
        </div>
        <div className="stat-card">
          <MessageSquare className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{formatNumber(summary?.recent?.length || 0)}</p>
          <p className="stat-label">Latest alerts loaded</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Send Notification */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Send Notification
            </CardTitle>
            <CardDescription>
              Broadcast notifications to users
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="notification-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_app">In-app</SelectItem>
                  <SelectItem value="push">Push, when the phone has a token</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Target Segment</Label>
              <Select value={segment} onValueChange={setSegment}>
                <SelectTrigger data-testid="notification-segment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="active">Active Users</SelectItem>
                  <SelectItem value="inactive">Inactive Users (7+ days)</SelectItem>
                  <SelectItem value="depositors">Depositing Users</SelectItem>
                  <SelectItem value="vip">Deposit balance ₹5,000+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input placeholder="Notification title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="notification-title" />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Enter your message..."
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="notification-message"
              />
            </div>

            <div className="flex gap-2">
              <Button className="flex-1" disabled={sending} onClick={send} data-testid="send-notification-btn">
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send now'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Notifications */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Recent Notifications</CardTitle>
            <CardDescription>Broadcasts you sent, and the latest alerts stored for players</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(summary?.broadcasts || []).length === 0 && (summary?.recent || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No alerts have been sent yet.</p>
              ) : null}
              {(summary?.broadcasts || []).map((notif) => (
                <div key={notif.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{notif.title}</p>
                    <p className="text-xs text-muted-foreground">{notif.segment} · {formatDateTime(notif.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm">{formatNumber(notif.sent || 0)}</p>
                    <p className="text-xs text-muted-foreground">players</p>
                  </div>
                </div>
              ))}
              {(summary?.recent || []).slice(0, 5).map((notif) => (
                <div key={notif.notification_id || notif.id} className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{notif.title}</p>
                  <p className="text-xs text-muted-foreground">{notif.body} · {formatDateTime(notif.created_at)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
